import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Rootkit Assembly (level 50) — the second Memory rung. Opcodes stream one at a time in stages; each
// stage is echoed back from the palette before the next one runs.
//
// TWO RULES THIS VIEW EXISTS TO KEEP:
//   • No scrollback, ever. Holding the opcodes IS the game, so nothing here records what went past —
//     not a history strip, not a "last stage" recap, nothing. The one exception is the row of opcodes
//     the player is currently tapping in, which is their own input.
//   • The pace is the server's. This view does not run a clock of its own: it polls and renders
//     whatever the server says is on screen, so there is no local schedule to drift or to replay from
//     zero on a resume. That is the trap Log Scrubber and Deep Core both fell into.
//
// The ticker below has EMPTY deps on purpose — a poll interval rebuilt on every state change never
// fires, which is exactly how Log Scrubber ended up showing one entry and jumping to the audit.

interface Challenge {
  started: boolean;
  /** "brief" | "ready" | "stream" | "echo" */
  phase: string;
  stageCount: number;
  stageIndex: number;
  stageLength: number;
  paceMs: number;
  /** Position in the stage of the opcode on screen, or -1 when none is. */
  showingIndex: number;
  /** Palette index of the opcode on screen, or -1 when none is. */
  showingOp: number;
  palette: string[];
  results: boolean[];
  rescansLeft: number;
  rescansGranted: number;
  stagesNeeded: number;
  timeLimitSec: number;
  readingAllowanceSec: number;
}

const POLL_MS = 120;

// `busy` is deliberately not taken: this view polls, so that flag is true most of the time and nothing
// here may depend on it. sendingRef is the re-entrancy guard.
export default function RootkitAssemblyView({
  challenge, onAct, onSubmit, setPendingSubmission,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [typed, setTyped] = useState<number[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const sendingRef = useRef(false);
  const pollingRef = useRef(false);

  useEffect(() => {
    setLive(challenge ?? null);
    setTyped([]);
    setNote(null);
    sendingRef.current = false;
  }, [challenge]);

  const absorb = useCallback((next: Challenge) => setLive(next), []);

  const started = live?.started ?? false;
  const phase = live?.phase ?? 'brief';

  // One player action at a time; polls are NOT counted, and nothing is gated on the host's `busy` —
  // this view polls several times a second and gating on it makes every button flicker and drops the
  // taps that land during a poll.
  const send = async (action: unknown) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setNote(null);
    try {
      const next = await onAct(action);
      if (next) absorb(next);
    } finally {
      sendingRef.current = false;
    }
  };

  // Kept up to date for the ticker below, which must not take `started` as a dependency: an interval
  // rebuilt on state changes is how Log Scrubber ended up never firing.
  const startedRef = useRef(false);
  useEffect(() => { startedRef.current = live?.started ?? false; }, [live]);

  useEffect(() => {
    const id = setInterval(() => {
      // Nothing changes on its own until the image is loaded, so do not ask.
      if (!startedRef.current || pollingRef.current || sendingRef.current) return;
      pollingRef.current = true;
      void (async () => {
        try {
          const next = await onAct({ kind: 'poll' });
          if (next) absorb(next);
        } finally {
          pollingRef.current = false;
        }
      })();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the tapped row whenever a new stage begins, so it never carries over.
  const stageIndex = live?.stageIndex ?? 0;
  useEffect(() => { setTyped([]); }, [stageIndex]);

  // The host submits this if the clock runs out: whatever has been assembled so far still counts.
  useEffect(() => { setPendingSubmission({}); }, [setPendingSubmission]);

  const palette = useMemo(() => live?.palette ?? [], [live]);
  const results = useMemo(() => live?.results ?? [], [live]);
  const stageLength = live?.stageLength ?? 0;
  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The kernel image is a run of <strong>opcodes</strong>. They come through one at a time, in{' '}
            <strong>{live?.stageCount ?? 3} stages</strong>, and after each stage you rebuild it by
            tapping the opcodes back <strong>in order</strong>.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Order is the whole job: <strong>opcodes repeat</strong> inside a stage, so remembering which
            ones went by is not enough. The palette also holds opcodes the image never uses, so it tells
            you nothing either.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Each stage is judged on its own — getting one wrong does not cost you the rest. You need{' '}
            <strong>{live?.stagesNeeded ?? 2} of {live?.stageCount ?? 3}</strong> clean.
          </p>
          {(live?.rescansGranted ?? 0) > 0 ? (
            <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem' }}>
              Your toolchain affords{' '}
              <strong>{live?.rescansGranted} rescan{(live?.rescansGranted ?? 0) === 1 ? '' : 's'}</strong>:
              a stage you have already watched, run through again. It costs no attempt — only the clock,
              which does not stop.
            </p>
          ) : (
            <p style={{ ...row, color: '#fbbf24', margin: '0 0 0.6rem' }}>
              Your Cracker and PortScanner are below the versions this needs — and the weaker of the two
              is what counts. <strong>No rescans</strong>: one pass per stage is all you get.
            </p>
          )}
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The stream starts the moment you load the image. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 30}s of it is set aside for reading this.
          </p>
          {/* NOT gated on `busy`. This view polls several times a second, so `busy` is true most of the
              time and the button spent more of it disabled than enabled — clicks landed in the gaps and
              were dropped. sendingRef inside send() is the real re-entrancy guard; it is the same
              mistake Load Balancer made, written down as an invariant, and then made again here. */}
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Load the kernel image
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <span style={{ ...row, minWidth: '7rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>STAGE </span>
          <strong>{stageIndex + 1}</strong>
          <span style={{ color: '#4b5563' }}> / {live?.stageCount ?? 0}</span>
        </span>
        {/* One slot per stage, all present from the start, so nothing shifts as they resolve. */}
        <span style={{ display: 'flex', gap: '0.3rem' }}>
          {Array.from({ length: live?.stageCount ?? 0 }, (_, i) => (
            <span key={i} style={{
              ...row, fontSize: '0.72rem', minWidth: '1.4rem', textAlign: 'center',
              color: i >= results.length ? '#4b5563' : results[i] ? '#4ade80' : '#f87171',
            }}>
              {i >= results.length ? '·' : results[i] ? '✓' : '✕'}
            </span>
          ))}
        </span>
        <span style={{ ...row, fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          {results.filter(Boolean).length} clean · {live?.stagesNeeded ?? 0} needed
        </span>
        <span style={{ ...row, fontSize: '0.74rem', minWidth: '6rem', color: (live?.rescansLeft ?? 0) > 0 ? '#60a5fa' : '#6b7280' }}>
          rescans {live?.rescansLeft ?? 0}/{live?.rescansGranted ?? 0}
        </span>
      </div>

      {/* The stage window. FIXED height: this is the thing the player stares at, and it must not move
          a pixel between an opcode showing and the gap after it. */}
      <div style={{
        border: `1px solid ${phase === 'stream' ? 'var(--primary-color)' : '#232833'}`,
        borderRadius: 4, background: phase === 'stream' ? '#12161d' : '#0d1117',
        height: '7rem', boxSizing: 'border-box', marginBottom: '0.6rem',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '0.5rem',
      }}>
        {phase === 'stream' && (
          <>
            <strong style={{
              fontFamily: 'monospace', fontSize: '2rem', letterSpacing: '0.1em',
              color: (live?.showingOp ?? -1) >= 0 ? '#e2e8f0' : '#1f2430',
            }}>
              {(live?.showingOp ?? -1) >= 0 ? palette[live!.showingOp] : '—'}
            </strong>
            <span style={{ display: 'flex', gap: '0.35rem' }}>
              {Array.from({ length: stageLength }, (_, i) => (
                <span key={i} style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: i <= (live?.showingIndex ?? -1) ? 'var(--primary-color)' : '#232833',
                }} />
              ))}
            </span>
          </>
        )}
        {phase === 'ready' && (
          <>
            <span style={{ ...row, color: '#cbd5e1', fontSize: '0.9rem' }}>
              stage {stageIndex + 1} of {live?.stageCount ?? 0} — {stageLength} opcodes
            </span>
            <button
              className="primary"
              onClick={() => void send({ kind: 'next' })}
              style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
            >
              Run the stage
            </button>
          </>
        )}
        {phase === 'echo' && (
          <>
            <span style={{ ...row, color: 'var(--text-muted)', fontSize: '0.72rem', textTransform: 'uppercase' }}>
              rebuild stage {stageIndex + 1}
            </span>
            <span style={{ display: 'flex', gap: '0.4rem', minHeight: '1.6rem', alignItems: 'center' }}>
              {Array.from({ length: stageLength }, (_, i) => (
                <span key={i} style={{
                  ...row, fontSize: '0.86rem', minWidth: '4.2rem', textAlign: 'center',
                  padding: '0.15rem 0.3rem', borderRadius: 3,
                  border: `1px solid ${i < typed.length ? '#166534' : '#232833'}`,
                  color: i < typed.length ? '#e2e8f0' : '#374151',
                }}>
                  {i < typed.length ? palette[typed[i]] : '?'}
                </span>
              ))}
            </span>
          </>
        )}
      </div>

      {/* The palette. Always mounted, so the board never reflows between phases. */}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
        {palette.map((name, i) => (
          <button
            key={i}
            onClick={() => {
              if (phase !== 'echo' || typed.length >= stageLength) return;
              setTyped(t => [...t, i]);
            }}
            disabled={phase !== 'echo'}
            style={{
              ...row, padding: '0.35rem 0.6rem', minWidth: '5rem',
              opacity: phase === 'echo' ? 1 : 0.35,
            }}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Reserved slot, so a refusal never moves the buttons under it. */}
      <div style={{ height: '1.2rem', marginBottom: '0.4rem', overflow: 'hidden' }}>
        {note && <span style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</span>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => {
            if (typed.length !== stageLength) {
              setNote(`${stageLength} opcodes needed — you have ${typed.length}`);
              return;
            }
            void send({ kind: 'echo', ops: typed });
          }}
          disabled={phase !== 'echo'}
          style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}
        >
          Commit stage
        </button>
        <button
          onClick={() => setTyped(t => t.slice(0, -1))}
          disabled={phase !== 'echo' || typed.length === 0}
          style={{ ...row, fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
        >
          Undo
        </button>
        {(live?.rescansGranted ?? 0) > 0 && (
          <button
            onClick={() => void send({ kind: 'rescan' })}
            disabled={phase !== 'echo' || (live?.rescansLeft ?? 0) === 0}
            style={{ ...row, fontSize: '0.78rem', padding: '0.25rem 0.6rem', color: '#60a5fa' }}
            title="Run this stage through again — the clock keeps going"
          >
            Rescan ({live?.rescansLeft ?? 0})
          </button>
        )}
        <button
          onClick={() => onSubmit({})}
          style={{ ...row, fontSize: '0.76rem', padding: '0.25rem 0.6rem' }}
          title="Stop here and count what assembled"
        >
          Stop here
        </button>
      </div>
    </div>
  );
}
